<template>
    <section class="p-5 bg-echoes-background-black">
        <!-- Header -->
        <UsersHeader>
            <template #title>{{ isEdit ? 'Edit User Group' : 'Create User Group' }}</template>
            <template #description>
                {{ isEdit ? 'Update the information for this user group.' : 'Register a new group to manage user access and permissions.' }}
            </template>
            <template #actions>
                <router-link to="/backoffice/groups"
                            class="inline-flex items-center px-4 py-2 rounded-xl bg-echoes-background-black/30 text-white hover:bg-white/10 transition-colors duration-200 border border-echoes-border-light">
                            <i class="mdi mdi-arrow-left mr-2"></i> Back to List
                        </router-link>
            </template>
        </UsersHeader>

        <div>
            <form @submit.prevent="submitCreateUserGroup">
                <!-- Card 1: Basic Information -->
                 <section class="grid grid-cols-2 gap-3">
                    <div class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-5 mb-6">
                    <h3 class="text-lg font-semibold text-white mb-4">Basic Information</h3>
                    <div class="grid grid-cols-1  gap-3">
                        <!-- Name -->
                        <div class="form-field">
                            <label for="name" class="block text-sm font-medium text-gray-200 mb-2">
                                Group Name <span class="text-red-500">*</span>
                            </label>
                            <TextInput 
                                id="name" 
                                v-model="form.name" 
                                placeholder="Enter the user group name" 
                                class="w-full" 
                                :required="true" 
                            />
                            <div v-if="errors.name" class="text-xs text-red-500 mt-1">{{ errors.name }}</div>
                        </div>

                        <!-- Access Code -->
                        <div class="form-field">
                            <label for="access_code" class="block text-sm font-medium text-gray-200 mb-2">
                                Access Code <span class="text-red-500">*</span>
                            </label>
                            <TextInput 
                                id="access_code" 
                                v-model="form.access_code" 
                                placeholder="Enter the access code" 
                                class="w-full" 
                                :required="true" 
                                pattern="^[a-zA-Z0-9-]+$" 
                                @input="onAccessCodeInput" 
                            />
                            <div v-if="errors.access_code" class="text-xs text-red-500 mt-1">{{ errors.access_code }}</div>
                        </div>
                    </div>
                </div>
                <!-- Card 3: System Configuration -->
                <div class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-5 mb-6 space-y-3">
                    <h3 class="text-lg font-semibold text-white mb-4">System Configuration</h3>
                    <div class="grid grid-cols-1 gap-3">
                        
                        <div class="grid grid-cols-3 gap-3">
                            <!-- Register Flow -->
                            <div class="form-field">
                                <label for="register_flow" class="block text-sm font-medium text-gray-200 mb-2">
                                    Register Flow
                                </label>
                                <SelectInput
                                    id="register_flow"
                                    v-model="form.register_flow"
                                    :options="registerFlowOptions"
                                    placeholder="Select the flow"
                                    class="w-full"
                                />
                                <div v-if="errors.register_flow" class="text-xs text-red-500 mt-1">{{ errors.register_flow }}</div>
                            </div>
                        <!-- Storage Limit -->
                        <div class="form-field">
                            <label for="storage_limit" class="block text-sm font-medium text-gray-200 mb-2">
                                Storage Limit
                            </label>
                            <TextInput 
                                id="storage_limit" 
                                v-model="form.storage_limit" 
                                placeholder="GB" 
                                min="10" 
                                class="w-full" 
                            />
                            <div v-if="errors.storage_limit" class="text-xs text-red-500 mt-1">{{ errors.storage_limit }}</div>
                        </div>

                        <!-- Default Credits -->
                        <div class="form-field">
                            <label for="default_credits" class="block text-sm font-medium text-gray-200 mb-2">
                                Default Credits
                            </label>
                            <TextInput 
                                id="default_credits" 
                                v-model="form.default_credits" 
                                placeholder="Enter default credits" 
                                class="w-full" 
                                type="number" 
                            />
                            <div v-if="errors.default_credits" class="text-xs text-red-500 mt-1">{{ errors.default_credits }}</div>
                        </div>
                    </div>

                    <!-- Onboarding Fields -->
                    <div class="form-field">
                        <label for="onboarding_fields" class="block text-sm font-medium text-gray-200 mb-2 w-full">
                            Onboarding Fields
                        </label>
                        <MultiTextInput 
                            v-model="form.onboarding_fields" 
                            placeholder="Enter onboarding field"
                            add-button-text="Add Field"
                            :min-items="0"
                            :select-options="onboardingFieldTypes"
                            select-placeholder="Select type"
                            select-width="w-full"
                        />
                        <div v-if="errors.onboarding_fields" class="text-xs text-red-500 mt-1">{{ errors.onboarding_fields }}</div>
                    </div>
                </div>
                </div>
                 </section>
             
                           
                <!-- Card 2: Permissions and Flags -->
                <div class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-5 mb-6">
                    <h3 class="text-lg font-semibold text-white mb-4">Permissions and Flags</h3>
                    <div class="grid grid-cols-2 gap-3 mb-6">
                        <!-- Permissions -->
                        <div class="form-field">
                            <label for="permissions" class="block text-sm font-medium text-gray-200 mb-2">
                                Permissions
                            </label>
                            <MultiSelect 
                                id="permissions" 
                                v-model="form.permissions" 
                                :options="permissionsOptions" 
                                placeholder="Select permissions" 
                                class="w-full" 
                            />
                            <div v-if="errors.permissions" class="text-xs text-red-500 mt-1">{{ errors.permissions }}</div>
                        </div>

                        <!-- Flags -->
                        <div class="form-field">
                            <label for="flags" class="block text-sm font-medium text-gray-200 mb-2">
                                Flags
                            </label>
                            <MultiSelect 
                                id="flags" 
                                v-model="form.flags" 
                                :options="flagsOptions" 
                                placeholder="Select flags" 
                                class="w-full" 
                            />
                            <div v-if="errors.flags" class="text-xs text-red-500 mt-1">{{ errors.flags }}</div>
                        </div>
                    </div>
                </div>

                

                <div class="flex items-center justify-center md:justify-end">
                    <button class="create-user-group-btn" :disabled="isSubmitting">
                        <ButtonLoading v-if="isSubmitting" :text="isEdit ? 'Updating...' : 'Creating...'" size="sm" />
                        <span v-else>{{ isEdit ? 'Update User Group' : 'Create User Group' }}</span>
                    </button>
                </div>
               
            </form>
        </div>
    </section>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from '@/plugins/axios';
import { useRouter, useRoute } from 'vue-router';
import { useLayoutComponents } from '@/composables/layout.js';
const { TextInput, MultiTextInput, ButtonLoading, MultiSelect, SelectInput } = useLayoutComponents();
import { useToast } from '@/composables/use-toast.js';
const { addToast } = useToast();

import UsersHeader from '@/pages/admin/AdminHeader.vue';

const router = useRouter();
const route = useRoute();

const form = ref({
    name: '',
    access_code: '',
    permissions:[],
    flags:[],
    register_flow: '',
    storage_limit: 10,
    default_credits: null,
    onboarding_fields: [],
});
const errors = ref({});
const permissionsOptions = ref([]);
const flagsOptions = ref([]);
const onboardingFieldsOptions = ref('');
const registerFlowOptions = ref([
    {label: "Buyer", value: "buyer"},
    {label: "Seller", value: "seller"},
    {label: "RenderyDAO", value: "rendery_dao"}
]);
const onboardingFieldTypes = ref([
    {label: "Text", value: "text"},
    {label: "Number", value: "number"},
]);

const fetchPermissions = async () => {
    const response = await axios.get('/api/permissions');
    permissionsOptions.value = response.data.map(permission => ({
        label: permission.name,
        value: permission.id
    }));
};

const fetchFlags = async () => {
    const response = await axios.get('/api/flags');
    flagsOptions.value = response.data.map(flag => ({
        label: flag.name,
        value: flag.id
    }));
};

const submitCreateUserGroup = async () => {
    errors.value = {};
    const accessCodeRegex = /^[a-zA-Z0-9-]+$/;
    if (!accessCodeRegex.test(form.value.access_code)) {
        errors.value.access_code = 'Access code must contain only letters, numbers and dashes.';
        return;
    }
    if (form.value.storage_limit < 10) {
        errors.value.storage_limit = 'Storage limit must be at least 10 GB.';
        return;
    }
    if (form.value.default_credits === '') {
        form.value.default_credits = null;
    }
    
    if (!Array.isArray(form.value.onboarding_fields)) {
        form.value.onboarding_fields = [];
    } else {
        form.value.onboarding_fields = form.value.onboarding_fields
            .map(field => {
                if (typeof field === 'object' && field !== null) {
                    if ('value' in field && !('field' in field)) {
                        return {
                            type: field.type || 'text',
                            field: field.value || ''
                        };
                    }
                    return {
                        type: field.type || 'text',
                        field: field.field || ''
                    };
                }
                if (typeof field === 'string') {
                    return {
                        type: 'text',
                        field: field
                    };
                }
                return null;
            })
            .filter(field => field !== null && field.field && field.field.trim() !== '');
    }
    
    const url = isEdit.value ? `/api/groups/${id.value}` : '/api/groups';
    await axios.post(url, form.value).then(response => {
        if (response.status === 201) {
            router.push('/backoffice/groups');
            addToast( isEdit.value ? 'User group updated successfully' : 'User group created successfully', 'success');
        }
    }).catch(error => {
        if (error.response.status === 409) {
            errors.value.access_code = 'Access code already exists.';
            addToast('Access code already exists', 'error');
        }
    });
};

const onAccessCodeInput = (event) => {
    const value = event.target.value.replace(/[^a-zA-Z0-9-]/g, '');
    form.value.access_code = value;
};

const fetchUserGroup = async () => {
    const response = await axios.get(`/api/groups/${id.value}`);
    form.value = response.data;
    form.value.flags = response.data.flagGroups.map(flagGroup => flagGroup.flag.id);
    form.value.permissions = response.data.permissionsGroups.map(permissionGroup => permissionGroup.permission.id);
    
    if (response.data?.onboarding_fields) {
        form.value.onboarding_fields = response.data.onboarding_fields.map(field => {
            if (typeof field === 'object' && field !== null && 'type' in field) {
                return {
                    type: field.type || 'text',
                    value: field.field || field.value || ''
                };
            }
            if (typeof field === 'string') {
                return { type: 'text', value: field };
            }
            return { type: 'text', value: String(field) };
        });
    } else {
        form.value.onboarding_fields = [];
    }
    onboardingFieldsOptions.value = response.data?.onboarding_fields? response.data.onboarding_fields : '';
};

const isEdit = ref(false);

const id = computed(() =>  route.params.id);

onMounted(() => {
    fetchPermissions();
    fetchFlags();
    if (id.value) {
        isEdit.value = true;
        fetchUserGroup();
    }
});
</script>

<style scoped lang="postcss">
.create-user-group-btn {
    @apply px-6 2xl:px-8 py-2 text-sm 2xl:text-base bg-echoes-primary text-white font-semibold rounded-xl hover:bg-echoes-primary/80 transition duration-200 flex items-center justify-center min-w-[180px] w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed border border-echoes-primary/30 shadow-lg hover:shadow-xl;
}

.form-field {
    @apply flex flex-col;
}

.form-field label {
    @apply text-sm font-medium text-gray-200 mb-2 transition-colors duration-200;
}

.form-field input:focus + label,
.form-field select:focus + label {
    @apply text-echoes-primary;
}


@media (max-width: 768px) {
    .form-field {
        @apply mb-4;
    }
    
    .bg-echoes-background-black\/20 {
        @apply p-4;
    }
}


.bg-echoes-background-black\/20:hover {
    @apply border-echoes-border-light/50 transition-all duration-300;
}


h3 {
    @apply text-lg font-semibold text-white mb-4 border-b border-echoes-border-light pb-2;
}
</style>
